import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';

import { EpcotConfig, GridConfig } from '@frontend/sports/common/client-config-data-access';
import { Column, DateGroup, EventGroup, FormGroup, GridGrouping, Group, LeagueGroup } from '@frontend/sports/grid/core/feature/model';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { DateHeaderComponent } from './date-header.component';
import { FormHeaderComponent } from './form-header.component';
import { GroupHeaderComponent } from './group-header.component';
import { LeagueHeaderComponent } from './league-header.component';

@Component({
    selector: 'ms-event-group',
    templateUrl: './event-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LeagueHeaderComponent, IconCustomComponent, GroupHeaderComponent, DateHeaderComponent, FormHeaderComponent],
})
export class EventGroupComponent implements OnChanges {
    @Input() columns: Column[] = [];
    @Input() group: EventGroup;
    @Input() grouping: GridGrouping;
    @Input() sportId: number;
    @Input() hideFavourites = false;
    @Input() hideMarketHeader = false;
    @Input() hideHeaderRegion = false;
    @Input() hideGroupCount = false;
    @Input() allowHeaderCollapse = true;
    @Input() competitionFallbackIcon?: string;
    @Output() toggle = new EventEmitter<EventGroup>();
    @HostBinding('class.event-group') eventGroupClass = true;

    groupingType = GridGrouping;
    header: boolean;
    options: boolean;
    hideGroupHeader = false;
    isEpcotEnabled = false;
    gridEventDisplayOverride = false;

    constructor() {
        const epcotConfig = inject(EpcotConfig);
        const gridConfig = inject(GridConfig);

        this.hideGroupHeader = epcotConfig.isEnabled && gridConfig.is2way3wayEnabled;
        this.gridEventDisplayOverride = gridConfig.gridEventDisplayVersion === 'V2';
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.columns) {
            this.options = this.columns.length > 0;

            // if it is the main market group placeholder we hide the options
            if (this.columns.length === 1 && this.columns[0].id === 'main') {
                this.options = false;
            }

            const groups = this.columns.map((column) => column.groups.find((group) => group.active));

            // if it is a non gridable group we hide the options
            if (groups.some((group) => !!group && group.extended)) {
                this.options = false;
            }
        }

        if (changes.columns || changes.grouping) {
            this.header = this.hideGroupHeader
                ? this.grouping !== GridGrouping.None && this.grouping !== GridGrouping.Date
                : this.grouping !== GridGrouping.None || this.options;
        }
    }

    @HostBinding('class.collapsible')
    get collapsible(): boolean {
        return this.group.collapsible;
    }

    @HostBinding('class.collapsed')
    get collapsed(): boolean {
        return this.group.collapsed;
    }

    checkGroup(group: EventGroup, grouping: 'League'): group is LeagueGroup;
    checkGroup(group: EventGroup, grouping: 'Date'): group is DateGroup;
    checkGroup(group: EventGroup, grouping: 'HomeForm' | 'AwayForm' | 'OverallForm'): group is FormGroup;
    checkGroup(_: EventGroup, grouping: keyof typeof GridGrouping): boolean {
        return this.grouping === GridGrouping[grouping];
    }

    activeGroup(column: Column): Group | undefined {
        return column.groups.find((group) => group.active);
    }

    toggleGroup(): void {
        if (this.group.collapsible && this.allowHeaderCollapse) {
            this.toggle.emit(this.group);
        }
    }
}
